/** Eight certificate-depth examples per part for Random Variables. */

export const PS_RV_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Build a pmf and check the axioms",
    setup: "$X$ is the number of heads in three fair coin tosses. Tabulate the pmf.",
    steps: [
      { text: "Sample space has $2^{3}=8$ equally likely outcomes.", why: "Independent fair tosses." },
      { text: "$X=0$ only for TTT: $P=\\tfrac18$.", why: "One outcome." },
      { text: "$X=1$ for HTT, THT, TTH: $P=\\tfrac38$.", why: "$\\binom31=3$ arrangements." },
      { text: "$X=2$ for HHT, HTH, THH: $P=\\tfrac38$.", why: "$\\binom32=3$." },
      { text: "$X=3$ only for HHH: $P=\\tfrac18$.", why: "One outcome." },
      { text: "All values are nonnegative — the first pmf axiom.", why: "Probabilities cannot be negative." },
      { text: "Total: $\\tfrac18+\\tfrac38+\\tfrac38+\\tfrac18=1$.", why: "Second axiom." },
      { text: "The shape matches $\\binom3k/8$, i.e. $X\\sim\\mathrm{Bin}(3,0.5)$.", why: "Recognize the family." },
    ],
    result: "$p(0)=p(3)=\\tfrac18$, $p(1)=p(2)=\\tfrac38$",
    check: "Symmetry about $1.5$ is expected because $p=0.5$.",
    mistake: "Assigning $\\tfrac14$ to each of the four values, which ignores how many outcomes give each count.",
    life: "Any pass/fail count — defective items per batch, dropped packets per second — starts life as exactly this kind of pmf table.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Expectation and variance from a pmf",
    setup: "$X$ takes values $1,2,3,4$ with probabilities $0.1,0.3,0.4,0.2$. Find $E[X]$, $\\operatorname{Var}(X)$, $\\sigma$.",
    steps: [
      { text: "$E[X]=\\sum xp(x)=1(0.1)+2(0.3)+3(0.4)+4(0.2)$.", why: "Definition of the mean." },
      { text: "$=0.1+0.6+1.2+0.8=2.7$.", why: "Add the products." },
      { text: "$E[X^{2}]=1(0.1)+4(0.3)+9(0.4)+16(0.2)$.", why: "Square the values, keep the same weights." },
      { text: "$=0.1+1.2+3.6+3.2=8.1$.", why: "Second moment." },
      { text: "$\\operatorname{Var}(X)=E[X^{2}]-(E[X])^{2}$.", why: "Shortcut formula." },
      { text: "$=8.1-2.7^{2}=8.1-7.29=0.81$.", why: "Subtract." },
      { text: "$\\sigma=\\sqrt{0.81}=0.9$.", why: "Standard deviation is in the original units." },
      { text: "Most of the distribution lies within $2.7\\pm0.9$, i.e. between $1.8$ and $3.6$.", why: "Interpretation." },
    ],
    result: "$E[X]=2.7$, $\\operatorname{Var}(X)=0.81$, $\\sigma=0.9$",
    check: "Direct definition: $\\sum(x-2.7)^{2}p(x)=0.289+0.147+0.036+0.338=0.81$.",
    mistake: "Computing $(E[X])^{2}-E[X^{2}]$, which gives a negative variance.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Binomial probabilities",
    setup: "A test has $10$ questions with $4$ options each, answered by guessing. Find $P(X=3)$ and $P(X\\ge1)$.",
    steps: [
      { text: "Each question is an independent trial with $p=\\tfrac14$, so $X\\sim\\mathrm{Bin}(10,0.25)$.", why: "Fixed $n$, constant $p$, independence." },
      { text: "$P(X=k)=\\binom{10}{k}p^{k}(1-p)^{10-k}$.", why: "Binomial pmf." },
      { text: "$\\binom{10}{3}=120$.", why: "Count the arrangements." },
      { text: "$p^{3}=0.015625$ and $0.75^{7}\\approx0.1335$.", why: "Powers." },
      { text: "$P(X=3)=120(0.015625)(0.1335)\\approx0.2503$.", why: "Multiply." },
      { text: "$P(X=0)=0.75^{10}\\approx0.0563$.", why: "All wrong." },
      { text: "$P(X\\ge1)=1-0.0563=0.9437$.", why: "Complement rule." },
      { text: "$E[X]=np=2.5$ and $\\sigma=\\sqrt{10(0.25)(0.75)}\\approx1.37$.", why: "Binomial moments." },
    ],
    result: "$P(X=3)\\approx0.250$ and $P(X\\ge1)\\approx0.944$",
    check: "$X=3$ is near the mean $2.5$, so a probability near $0.25$ is plausible.",
    mistake: "Forgetting the $\\binom{10}{3}$ factor and reporting $0.00209$.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Poisson counts",
    setup: "A helpline receives calls at $\\lambda=3$ per hour. Find $P(X=0)$, $P(X\\le2)$, and $P(X\\ge5)$ in one hour.",
    steps: [
      { text: "$X\\sim\\mathrm{Poisson}(3)$ with $P(X=k)=\\dfrac{e^{-3}3^{k}}{k!}$.", why: "Rare events at a constant rate." },
      { text: "$e^{-3}\\approx0.049787$.", why: "Needed in every term." },
      { text: "$P(X=0)=0.049787$.", why: "$3^{0}/0!=1$." },
      { text: "$P(X=1)=0.049787(3)=0.149361$.", why: "$k=1$." },
      { text: "$P(X=2)=0.049787\\cdot\\tfrac92=0.224042$.", why: "$3^{2}/2!=4.5$." },
      { text: "$P(X\\le2)=0.049787+0.149361+0.224042\\approx0.4232$.", why: "Add the three terms." },
      { text: "$P(X=3)\\approx0.224042$ and $P(X=4)\\approx0.168031$.", why: "Two more terms for the upper tail." },
      { text: "$P(X\\ge5)=1-(0.4232+0.224042+0.168031)\\approx0.1847$.", why: "Complement of the first five terms." },
    ],
    result: "$P(X=0)\\approx0.0498$, $P(X\\le2)\\approx0.423$, $P(X\\ge5)\\approx0.185$",
    check: "For Poisson, mean $=$ variance $=3$, and the mode is at $k=2$ and $3$ — matching the largest terms.",
    mistake: "Using $\\lambda$ for a different time window without rescaling ($\\lambda=1.5$ for half an hour).",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Geometric waiting time",
    setup: "A machine part fails on any given day with probability $0.05$. Let $X$ be the day of the first failure. Find $P(X=4)$, $P(X>10)$, $E[X]$.",
    steps: [
      { text: "$X\\sim\\mathrm{Geometric}(0.05)$ counting trials until the first success.", why: "Independent daily trials." },
      { text: "$P(X=k)=(1-p)^{k-1}p$.", why: "Three failures then a success, in order." },
      { text: "$P(X=4)=0.95^{3}(0.05)$.", why: "Substitute $k=4$." },
      { text: "$0.95^{3}=0.857375$, so $P(X=4)\\approx0.0429$.", why: "Multiply." },
      { text: "$P(X>10)=0.95^{10}$.", why: "No failure in the first ten days." },
      { text: "$0.95^{10}\\approx0.5987$.", why: "Compute the power." },
      { text: "$E[X]=1/p=20$ days.", why: "Mean of a geometric distribution." },
      { text: "Memorylessness: given survival to day $10$, the expected further wait is still $20$ days.", why: "Characteristic property." },
    ],
    result: "$P(X=4)\\approx0.043$, $P(X>10)\\approx0.599$, $E[X]=20$ days",
    check: "$\\operatorname{Var}(X)=(1-p)/p^{2}=380$, so $\\sigma\\approx19.5$ — a very spread-out wait.",
    mistake: "Using $p^{k-1}(1-p)$, which swaps the roles of success and failure.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Linear transformations and sums",
    setup: "$X$ has $E[X]=10$, $\\operatorname{Var}(X)=4$; $Y$ has $E[Y]=6$, $\\operatorname{Var}(Y)=9$; they are independent. Find the mean and variance of $3X-2Y+5$.",
    steps: [
      { text: "Linearity of expectation: $E[3X-2Y+5]=3E[X]-2E[Y]+5$.", why: "Holds with or without independence." },
      { text: "$=3(10)-2(6)+5=30-12+5=23$.", why: "Substitute." },
      { text: "Variance ignores additive constants: $\\operatorname{Var}(W+5)=\\operatorname{Var}(W)$.", why: "Shifting does not change spread." },
      { text: "Scaling squares: $\\operatorname{Var}(3X)=9\\operatorname{Var}(X)=36$.", why: "$\\operatorname{Var}(aX)=a^{2}\\operatorname{Var}(X)$." },
      { text: "$\\operatorname{Var}(-2Y)=4\\operatorname{Var}(Y)=36$.", why: "The sign disappears when squared." },
      { text: "Independence gives $\\operatorname{Cov}(X,Y)=0$, so variances add.", why: "Cross term vanishes." },
      { text: "$\\operatorname{Var}(3X-2Y+5)=36+36=72$.", why: "Add." },
      { text: "$\\sigma=\\sqrt{72}=6\\sqrt2\\approx8.49$.", why: "Standard deviation." },
    ],
    result: "Mean $23$, variance $72$, $\\sigma\\approx8.49$",
    check: "If instead $\\operatorname{Cov}(X,Y)=2$, the variance would be $72-12(2)=48$ — independence matters.",
    mistake: "Writing $\\operatorname{Var}(3X-2Y)=3\\operatorname{Var}(X)-2\\operatorname{Var}(Y)$; variances never subtract for independent terms.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Expected value of a game",
    setup: "A game costs $2$ dollars to play. You win $10$ dollars with probability $0.1$, $3$ dollars with probability $0.25$, otherwise nothing. Should you play?",
    steps: [
      { text: "Let $G$ be the gross winnings; $P(G=10)=0.1$, $P(G=3)=0.25$, $P(G=0)=0.65$.", why: "Probabilities must sum to $1$." },
      { text: "$E[G]=10(0.1)+3(0.25)+0(0.65)$.", why: "Definition." },
      { text: "$=1+0.75=1.75$ dollars.", why: "Add." },
      { text: "Net profit is $N=G-2$, so $E[N]=1.75-2=-0.25$.", why: "Linearity with a constant cost." },
      { text: "Expected loss is $25$ cents per play, so the game is unfavourable.", why: "Decision rule." },
      { text: "$E[G^{2}]=100(0.1)+9(0.25)=10+2.25=12.25$.", why: "Prepare the variance." },
      { text: "$\\operatorname{Var}(G)=12.25-1.75^{2}=12.25-3.0625=9.1875$, so $\\sigma\\approx3.03$.", why: "Volatility per play." },
      { text: "Over $100$ plays expect about $-25$ dollars with standard deviation $\\sqrt{100}(3.03)\\approx30$ dollars.", why: "Independent plays add variances." },
    ],
    result: "$E[\\text{net}]=-0.25$ dollars per play — do not play",
    check: "A fair price would be $1.75$ dollars, exactly the expected gross winnings.",
    mistake: "Comparing the $10$ dollar prize with the $2$ dollar cost and calling the game favourable.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Joint pmf, marginals, and independence",
    setup: "$P(X=x,Y=y)$ is $0.1,0.2,0.3$ for $(1,1),(1,2),(2,1)$ and $0.4$ for $(2,2)$. Find the marginals, $E[XY]$, and test independence.",
    steps: [
      { text: "Marginal of $X$: $P(X=1)=0.1+0.2=0.3$.", why: "Sum over $y$." },
      { text: "$P(X=2)=0.3+0.4=0.7$.", why: "Same row logic." },
      { text: "Marginal of $Y$: $P(Y=1)=0.1+0.3=0.4$, $P(Y=2)=0.2+0.4=0.6$.", why: "Sum over $x$." },
      { text: "Independence test: $P(X=1)P(Y=1)=0.3(0.4)=0.12\\ne0.1$.", why: "Compare with the joint entry." },
      { text: "So $X$ and $Y$ are dependent.", why: "One failed cell is enough." },
      { text: "$E[X]=1(0.3)+2(0.7)=1.7$ and $E[Y]=1(0.4)+2(0.6)=1.6$.", why: "From the marginals." },
      { text: "$E[XY]=1(0.1)+2(0.2)+2(0.3)+4(0.4)=0.1+0.4+0.6+1.6=2.7$.", why: "Sum $xy$ against joint probabilities." },
      { text: "$\\operatorname{Cov}(X,Y)=2.7-1.7(1.6)=2.7-2.72=-0.02$.", why: "Slight negative association." },
    ],
    result: "Dependent, with $E[XY]=2.7$ and $\\operatorname{Cov}(X,Y)=-0.02$",
    check: "All four joint probabilities sum to $1$, and each marginal sums to $1$.",
    mistake: "Concluding independence from a near-zero covariance; zero covariance does not imply independence either.",
  },
];

export const PS_RV_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Find the constant in a pdf",
    setup: "$f(x)=cx$ on $[0,4]$ and $0$ elsewhere. Find $c$, then $P(1\\le X\\le2)$.",
    steps: [
      { text: "A pdf must integrate to $1$: $\\int_0^4 cx\\,dx=1$.", why: "Normalization condition." },
      { text: "$\\int_0^4 x\\,dx=\\left[\\tfrac{x^{2}}{2}\\right]_0^4=8$.", why: "Antiderivative." },
      { text: "So $8c=1$ and $c=\\tfrac18$.", why: "Solve for the constant." },
      { text: "$f(x)=\\tfrac{x}{8}$ is nonnegative on $[0,4]$, so it is a valid density.", why: "Second pdf requirement." },
      { text: "$P(1\\le X\\le2)=\\int_1^2\\tfrac{x}{8}\\,dx$.", why: "Probability is area under the density." },
      { text: "$=\\tfrac{1}{16}\\left[x^{2}\\right]_1^2=\\tfrac{4-1}{16}$.", why: "Evaluate." },
      { text: "$=\\tfrac{3}{16}=0.1875$.", why: "Simplify." },
      { text: "Note $P(X=1.5)=0$: single points carry no probability for continuous variables.", why: "Key contrast with the discrete case." },
    ],
    result: "$c=\\tfrac18$ and $P(1\\le X\\le2)=\\tfrac{3}{16}$",
    check: "$P(0\\le X\\le4)=16/16=1$.",
    mistake: "Treating $f(2)=0.25$ as a probability; densities can even exceed $1$.",
    life: "Wind-speed and rainfall models are densities like this: only intervals carry probability, which is why forecasts speak of ranges.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "From pdf to cdf and back",
    setup: "For $f(x)=\\tfrac{x}{8}$ on $[0,4]$, find $F(x)$, the median, and verify $F'=f$.",
    steps: [
      { text: "$F(x)=\\int_0^x\\tfrac{t}{8}\\,dt$ for $0\\le x\\le4$.", why: "Definition of the cdf." },
      { text: "$=\\tfrac{x^{2}}{16}$.", why: "Integrate." },
      { text: "$F(0)=0$ and $F(4)=1$, and $F$ is increasing.", why: "All cdf requirements hold." },
      { text: "Median $m$ solves $F(m)=0.5$.", why: "Definition of the median." },
      { text: "$\\tfrac{m^{2}}{16}=0.5\\Rightarrow m^{2}=8$.", why: "Multiply through." },
      { text: "$m=2\\sqrt2\\approx2.83$.", why: "Positive root only." },
      { text: "Differentiate back: $F'(x)=\\tfrac{2x}{16}=\\tfrac{x}{8}=f(x)$.", why: "Fundamental theorem of calculus." },
      { text: "The median exceeds the midpoint $2$ because the density leans right.", why: "Shape interpretation." },
    ],
    result: "$F(x)=x^{2}/16$ on $[0,4]$ with median $2\\sqrt2\\approx2.83$",
    check: "$P(X\\le2)=4/16=0.25$, so the first quartile is exactly $2$.",
    mistake: "Forgetting to state $F(x)=0$ for $x<0$ and $F(x)=1$ for $x>4$.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Mean and variance of a continuous variable",
    setup: "For $f(x)=\\tfrac{x}{8}$ on $[0,4]$, compute $E[X]$, $E[X^{2}]$, and $\\operatorname{Var}(X)$.",
    steps: [
      { text: "$E[X]=\\int_0^4 x\\cdot\\tfrac{x}{8}\\,dx=\\tfrac18\\int_0^4x^{2}\\,dx$.", why: "Definition with the density as weight." },
      { text: "$\\int_0^4x^{2}\\,dx=\\tfrac{64}{3}$.", why: "Antiderivative $x^{3}/3$." },
      { text: "$E[X]=\\tfrac{64}{24}=\\tfrac83\\approx2.667$.", why: "Divide by $8$." },
      { text: "$E[X^{2}]=\\tfrac18\\int_0^4x^{3}\\,dx$.", why: "Second moment." },
      { text: "$\\int_0^4x^{3}\\,dx=\\tfrac{256}{4}=64$, so $E[X^{2}]=8$.", why: "Evaluate." },
      { text: "$\\operatorname{Var}(X)=8-\\left(\\tfrac83\\right)^{2}=8-\\tfrac{64}{9}$.", why: "Shortcut formula." },
      { text: "$=\\tfrac{72-64}{9}=\\tfrac89\\approx0.889$.", why: "Common denominator." },
      { text: "$\\sigma=\\sqrt{8/9}=\\tfrac{2\\sqrt2}{3}\\approx0.943$.", why: "Standard deviation." },
    ],
    result: "$E[X]=\\tfrac83$, $\\operatorname{Var}(X)=\\tfrac89$, $\\sigma\\approx0.943$",
    check: "The mean $2.67$ sits just below the median $2.83$, the signature of a left-skewed density on $[0,4]$.",
    mistake: "Integrating $f$ instead of $xf(x)$ when computing the mean.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Normal probabilities via z-scores",
    setup: "Heights are $N(170,8^{2})$ cm. Find $P(X<180)$, $P(162<X<178)$, and the $90$th percentile.",
    steps: [
      { text: "Standardize: $z=\\dfrac{x-\\mu}{\\sigma}$.", why: "Converts to the standard normal table." },
      { text: "For $x=180$: $z=\\dfrac{10}{8}=1.25$.", why: "Substitute." },
      { text: "$\\Phi(1.25)\\approx0.8944$, so $P(X<180)\\approx0.894$.", why: "Table lookup." },
      { text: "For $162$: $z=-1$; for $178$: $z=1$.", why: "Symmetric interval of one $\\sigma$." },
      { text: "$P(-1<Z<1)=0.8413-0.1587=0.6826$.", why: "Difference of cdf values." },
      { text: "So about $68\\%$ of heights lie in $162$–$178$ cm — the empirical rule.", why: "Sanity anchor." },
      { text: "For the $90$th percentile, $z_{0.90}\\approx1.2816$.", why: "Inverse normal." },
      { text: "$x=170+1.2816(8)\\approx180.3$ cm.", why: "Unstandardize." },
    ],
    result: "$P(X<180)\\approx0.894$, $P(162<X<178)\\approx0.683$, $P_{90}\\approx180.3$ cm",
    check: "The $90$th percentile lies just above $180$, consistent with $P(X<180)=0.894$.",
    mistake: "Dividing by the variance $64$ instead of the standard deviation $8$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Exponential lifetimes and memorylessness",
    setup: "A bulb's lifetime is exponential with mean $1000$ hours. Find $P(X>1500)$ and $P(X>2500\\mid X>1000)$.",
    steps: [
      { text: "Mean $=1/\\lambda=1000$, so $\\lambda=0.001$ per hour.", why: "Rate parameter." },
      { text: "Survival function: $P(X>t)=e^{-\\lambda t}$.", why: "Standard exponential result." },
      { text: "$P(X>1500)=e^{-1.5}\\approx0.2231$.", why: "Substitute $t=1500$." },
      { text: "Conditional: $P(X>2500\\mid X>1000)=\\dfrac{P(X>2500)}{P(X>1000)}$.", why: "Definition of conditioning, using nesting." },
      { text: "$=\\dfrac{e^{-2.5}}{e^{-1}}=e^{-1.5}$.", why: "Exponents subtract." },
      { text: "$\\approx0.2231$ — identical to $P(X>1500)$.", why: "Memorylessness." },
      { text: "A used bulb is statistically as good as new; no wear-out is modelled.", why: "Interpretation and limitation." },
      { text: "Median lifetime: $e^{-\\lambda m}=0.5\\Rightarrow m=\\ln2/\\lambda\\approx693$ hours, well below the mean.", why: "Right-skewed distribution." },
    ],
    result: "$P(X>1500)\\approx0.223$ and the conditional probability is the same $0.223$",
    check: "$\\sigma=1/\\lambda=1000$ equals the mean, a signature of the exponential family.",
    mistake: "Using the normal table for a skewed exponential lifetime.",
  },
  {
    number: 6,
    tier: "Hard",
    title: "Normal approximation to the binomial",
    setup: "$X\\sim\\mathrm{Bin}(100,0.4)$. Estimate $P(X\\le45)$ with a continuity correction.",
    steps: [
      { text: "$\\mu=np=40$ and $\\sigma^{2}=np(1-p)=24$.", why: "Binomial moments." },
      { text: "$\\sigma=\\sqrt{24}\\approx4.899$.", why: "Standard deviation." },
      { text: "Check validity: $np=40\\ge5$ and $n(1-p)=60\\ge5$.", why: "Approximation criteria." },
      { text: "Continuity correction: use $45.5$ because $X$ is integer-valued.", why: "Bridges discrete to continuous." },
      { text: "$z=\\dfrac{45.5-40}{4.899}\\approx1.1227$.", why: "Standardize." },
      { text: "$\\Phi(1.12)\\approx0.8686$.", why: "Table lookup." },
      { text: "So $P(X\\le45)\\approx0.869$.", why: "Approximate answer." },
      { text: "The exact binomial value is $0.8689$, so the error is under $0.001$.", why: "Correction makes the fit excellent." },
    ],
    result: "$P(X\\le45)\\approx0.869$",
    check: "Without the correction, $z=1.0206$ gives $0.846$ — noticeably worse.",
    mistake: "Skipping the continuity correction, which biases the estimate downward here.",
  },
  {
    number: 7,
    tier: "Hard",
    title: "Transformation of a random variable",
    setup: "$U\\sim\\mathrm{Uniform}(0,1)$ and $X=-\\tfrac{1}{2}\\ln U$. Find the distribution of $X$.",
    steps: [
      { text: "$X$ is positive because $\\ln U<0$ for $0<U<1$.", why: "Range check first." },
      { text: "Compute the cdf: $P(X\\le x)=P\\left(-\\tfrac12\\ln U\\le x\\right)$.", why: "Work with the cdf, not the pdf." },
      { text: "Rearrange: $\\ln U\\ge-2x$.", why: "Multiplying by $-2$ flips the inequality." },
      { text: "Exponentiate: $U\\ge e^{-2x}$.", why: "$\\exp$ is increasing." },
      { text: "For uniform $U$, $P(U\\ge u)=1-u$.", why: "Uniform cdf." },
      { text: "So $F_X(x)=1-e^{-2x}$ for $x>0$.", why: "Substitute $u=e^{-2x}$." },
      { text: "That is the exponential cdf with $\\lambda=2$; differentiate to get $f_X(x)=2e^{-2x}$.", why: "Identify the family." },
      { text: "Hence $E[X]=\\tfrac12$ and $\\operatorname{Var}(X)=\\tfrac14$.", why: "Exponential moments." },
    ],
    result: "$X\\sim\\mathrm{Exponential}(\\lambda=2)$ with mean $0.5$",
    check: "$P(X\\le0.5)=1-e^{-1}\\approx0.632$, matching the standard exponential value.",
    mistake: "Substituting the density directly without the Jacobian or the cdf method.",
  },
  {
    number: 8,
    tier: "Hard",
    title: "Central limit theorem for a sample mean",
    setup: "A population has $\\mu=50$, $\\sigma=12$ and is skewed. For $n=36$, find $P(\\bar{X}>53)$ and a symmetric $95\\%$ range.",
    steps: [
      { text: "The CLT applies because $n=36\\ge30$, so $\\bar{X}$ is approximately normal.", why: "Skewness of the parent matters less as $n$ grows." },
      { text: "$E[\\bar{X}]=\\mu=50$.", why: "The sample mean is unbiased." },
      { text: "Standard error: $\\sigma/\\sqrt{n}=12/6=2$.", why: "Averaging shrinks spread by $\\sqrt{n}$." },
      { text: "So $\\bar{X}\\approx N(50,2^{2})$.", why: "Sampling distribution." },
      { text: "$z=\\dfrac{53-50}{2}=1.5$.", why: "Standardize." },
      { text: "$P(\\bar{X}>53)=1-\\Phi(1.5)\\approx1-0.9332=0.0668$.", why: "Upper tail." },
      { text: "A $95\\%$ range is $50\\pm1.96(2)$.", why: "Central $95\\%$ of a normal." },
      { text: "$=[46.08,\\,53.92]$.", why: "Compute the endpoints." },
    ],
    result: "$P(\\bar{X}>53)\\approx0.067$, with $95\\%$ of sample means in $[46.1,53.9]$",
    check: "A single observation has $z=(53-50)/12=0.25$, so $P\\approx0.40$ — averaging sharply reduces variability.",
    mistake: "Using $\\sigma=12$ instead of the standard error $2$ when standardizing a mean.",
  },
];
